import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client.ts';
import { queryDefaults } from './queries.ts';
import { useSession } from '../auth/session.tsx';

/** One welfare visit as it stands (125), and whether this person may edit or withdraw it. */
export interface VisitDetail {
  id: string;
  client_id: string;
  visit_date: string;
  visit_type: string;
  duration_minutes: number | null;
  appearance: string | null;
  mood: string | null;
  affect: string | null;
  cognitive_status: string | null;
  health_flag: string | null;
  summary: string | null;
  is_migrated: boolean;
  withdrawn: boolean;
  withdrawal_reason: string | null;
  current_version: number;
  at_stay: boolean;
  visited_by: string | null;
  recorded_by: string | null;
  may_edit: boolean;
  /** How many times it has been edited (a withdrawal is not an edit). */
  edits: number;
}

/** What a version changed from the one before (125): a field, its old and its new value. */
export interface FieldChange {
  field: string;
  label: string;
  before: string | null;
  after: string | null;
}

/** One version of a visit, newest first: as first recorded, an edit with its reason, or a withdrawal. */
export interface VisitVersion {
  version: number;
  kind: 'as recorded' | 'edit' | 'withdrawal';
  reason: string | null;
  reason_label: string | null;
  reason_text: string | null;
  withdrawal_reason: string | null;
  recorded_by: string | null;
  recorded_at: string;
  changes: FieldChange[];
}

/** A welfare visit corrected: what changes (left out keeps its value) and why. */
export interface VisitEditBody {
  visit_date?: string;
  visit_type?: string;
  location?: 'placement' | 'stay';
  duration_minutes?: number | null;
  appearance?: string | null;
  mood?: string | null;
  affect?: string | null;
  cognitive_status?: string | null;
  health_flag?: string | null;
  summary?: string | null;
  reason: string;
  reason_text?: string;
}

function post<T>(getToken: () => Promise<string>, path: string, body: unknown) {
  return apiFetch<T>(path, getToken, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export function useVisit(id: string | null) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['visit', id],
    queryFn: () => apiFetch<{ visit: VisitDetail }>(`/visits/${id}`, getToken),
    ...queryDefaults,
    enabled: !!id,
  });
}

export function useVisitVersions(id: string, enabled: boolean) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['visit', id, 'versions'],
    queryFn: () => apiFetch<{ versions: VisitVersion[] }>(`/visits/${id}/versions`, getToken),
    ...queryDefaults,
    enabled,
  });
}

/** After an edit or a withdrawal, everything that counts or lists visits reads again. */
function useVisitChanged(id: string) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['visit', id] });
    void queryClient.invalidateQueries({ queryKey: ['client'] });
    void queryClient.invalidateQueries({ queryKey: ['visits'] });
    void queryClient.invalidateQueries({ queryKey: ['me'] });
  };
}

export function useEditVisit(id: string) {
  const { getToken } = useSession();
  const changed = useVisitChanged(id);
  return useMutation({
    mutationFn: (body: VisitEditBody) => post<{ visit: VisitDetail; version: number }>(getToken, `/visits/${id}/edit`, body),
    onSuccess: changed,
  });
}

export function useWithdrawVisit(id: string) {
  const { getToken } = useSession();
  const changed = useVisitChanged(id);
  return useMutation({
    mutationFn: (reason: string) => post<{ visit: VisitDetail; version: number }>(getToken, `/visits/${id}/withdraw`, { reason }),
    onSuccess: changed,
  });
}

/** A property site visit corrected (125): its date, who visited, its note, and why. */
export interface PropertyVisitEditBody {
  visited_on?: string;
  visited_by_user_id?: string;
  note?: string | null;
  /** Documents were retrieved (151, A-#154). Ticking it on the property's first visit makes the Finance task (A-#157). */
  documents_retrieved?: boolean;
  reason: string;
  reason_text?: string;
}

export function usePropertyVisitVersions(id: string, enabled: boolean) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['property-visit', id, 'versions'],
    queryFn: () => apiFetch<{ versions: VisitVersion[] }>(`/property-visits/${id}/versions`, getToken),
    ...queryDefaults,
    enabled,
  });
}

export function useEditPropertyVisit(propertyId: string, visitId: string) {
  const { getToken } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PropertyVisitEditBody) => post<{ version: number }>(getToken, `/property-visits/${visitId}/edit`, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['property', propertyId] });
      void queryClient.invalidateQueries({ queryKey: ['property-visit', visitId] });
      void queryClient.invalidateQueries({ queryKey: ['properties'] });
      void queryClient.invalidateQueries({ queryKey: ['client'] });
    },
  });
}
